import {
  deleteDsdEditorMappingReview,
  inheritDsdEditorMappingReviews,
  saveDsdEditorMappingReview,
  type DsdEditorMappingReview,
  type DsdEditorMappingReviewJudgement,
  type DsdEditorMappingReviewTarget,
  type DsdEditorReportResponse,
} from "@/apis/dsd-editor/route";
import queries from "@/queries";
import { sameMappingReviewPair } from "@/utils/dsd-editor/note-reference-review";
import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import { DSD_REVIEW_COPY } from "./copy";

const CONFLICT_MESSAGE =
  "다른 사용자가 같은 항목을 먼저 판단했습니다. 최신 상태를 불러옵니다.";

export type DsdEditorMappingReviewInput = {
  target: DsdEditorMappingReviewTarget;
  judgement: DsdEditorMappingReviewJudgement;
  reason: string;
};

/**
 * 주석 참조 검토 판단 기록/해제.
 * 서버 응답이 곧 최신 판단이라 무거운 preview 전체를 다시 받지 않고 캐시된 report에서
 * 같은 쌍(relation + 좌표)의 review만 바꾼다.
 */
export const useDsdEditorMappingReview = (documentId: string) => {
  const queryClient = useQueryClient();
  // report는 분석 옵션별로 키가 갈린다 — prefix로 전부 갱신
  const reportQueryKey = [...queries.dsdEditor(documentId).queryKey, "report"];
  const patchCachedReview = (
    pair: { relation: string; coordinates: string[] },
    review: DsdEditorMappingReview | null,
  ) =>
    queryClient.setQueriesData<DsdEditorReportResponse>(
      { queryKey: reportQueryKey },
      (report) =>
        report && {
          ...report,
          noteReferenceChecks: report.noteReferenceChecks?.map((check) => ({
            ...check,
            reviewTargets: check.reviewTargets.map((target) =>
              sameMappingReviewPair(target, pair)
                ? { ...target, review }
                : target,
            ),
          })),
        },
    );
  const onError = (error: unknown) => {
    if (isAxiosError(error) && error.response?.status === 409) {
      toast.error(CONFLICT_MESSAGE);
      void queryClient.invalidateQueries(queries.dsdEditor(documentId));
      return;
    }
    Sentry.captureException(error);
    toast.error("검토 판단 저장에 실패했습니다. 다시 시도해 주세요.");
  };
  const save = useMutation({
    mutationFn: ({ target, judgement, reason }: DsdEditorMappingReviewInput) =>
      saveDsdEditorMappingReview(documentId, {
        relation: target.relation,
        coordinates: target.coordinates,
        judgement,
        reason: reason || undefined,
      }),
    onSuccess: (review) => patchCachedReview(review, review),
    onError,
  });
  const remove = useMutation({
    mutationFn: (review: DsdEditorMappingReview) =>
      deleteDsdEditorMappingReview(documentId, review.id),
    onSuccess: (_, review) => patchCachedReview(review, null),
    onError,
  });

  // 모달이 완료를 기다렸다가 닫을 수 있도록 promise를 돌려준다 (실패 토스트는 onError가 처리)
  return { save: save.mutateAsync, remove: remove.mutateAsync };
};

/**
 * 다른 DSD의 판단을 이 문서로 가져온다. 무엇이 넘어왔는지 응답에 없어 캐시 패치 대신 report를 다시 받는다.
 * 실패 토스트는 여기서 처리한다 — 호출측은 성공할 때만 모달을 닫는다.
 */
export const useInheritDsdEditorMappingReviews = (documentId: string) => {
  const queryClient = useQueryClient();
  const copy = DSD_REVIEW_COPY.reviewImport;
  const refetchReport = () =>
    queryClient.invalidateQueries({
      queryKey: [...queries.dsdEditor(documentId).queryKey, "report"],
    });

  return useMutation({
    mutationFn: (sourceReportId: string) =>
      inheritDsdEditorMappingReviews(documentId, sourceReportId),
    onSuccess: () => {
      toast.success(copy.success);
      return refetchReport();
    },
    onError: (error) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 422) {
        toast.error(copy.sourceRejected);
        return;
      }
      if (status === 409) {
        toast.error(CONFLICT_MESSAGE);
        void refetchReport();
        return;
      }
      Sentry.captureException(error);
      toast.error(copy.failed);
    },
  });
};
